import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { TableCell } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';

import {
  selectIsLoanLocked,
  selectIsRelockRequiredFromPricingStatus,
  selectProgramAndPricingApplicationData,
} from 'store/loanApplications/selectors';

import { getRateTableCellStyles } from './RateTableCell.styles';
import {
  getIsCellApplicable,
  getIsCellSelected,
  getLoanOptionPendingData,
} from './RateTableCell.utils';

const RateTableCell = ({
  onSelectLoanProgramOption,
  selectedLoanProgramOption,
  cellData,
  pendingChanges,
  lockTerm,
  programId,
  programDetails,
  noteRate,
  isProgramExpired = false,
  isProgramIneligible = false,
}) => {
  const isExpiredPricingSaveEnabled = useFeatureToggle(FeatureFlag.EnableExpiredPricingSave);

  const application = useSelector(selectProgramAndPricingApplicationData);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const isObLoanRelockRequired = useSelector(selectIsRelockRequiredFromPricingStatus);

  const finalBuyPrice = _.get(cellData, ['finalBuyPrice'], '-');
  const program = _.get(programDetails, ['program']);

  const isApplicable = getIsCellApplicable({
    cellData,
    program,
    lockTerm,
    loanLockTerm: application.lockTerm,
    isLoanLocked,
    isProgramIneligible,
    isObLoanRelockRequired,
    isProgramExpired,
    isExpiredPricingSaveEnabled,
  });
  const isSelected = getIsCellSelected({
    lockTerm,
    finalBuyPrice,
    selectedLoanProgramOption,
    noteRate,
    programId,
  });

  const { isInitLoanOptionId, isNewLoanOptionId } = getLoanOptionPendingData({
    pendingChanges,
    application,
    lockTerm,
    finalBuyPrice,
    programId,
    noteRate,
  });

  const onClick = useCallback(() => {
    if (!isApplicable) {
      return;
    }

    onSelectLoanProgramOption({ ...cellData, lockTerm, programId, rate: noteRate });
  }, [cellData, onSelectLoanProgramOption, lockTerm, isApplicable, programId, noteRate]);

  return (
    <TableCell
      align="right"
      data-id={cellData?.loanOptionId}
      sx={getRateTableCellStyles({
        isApplicable,
        isSelected,
        isInitLoanOptionId,
        isNewLoanOptionId,
      })}
      onClick={onClick}
    >
      <span>{finalBuyPrice}</span>

      {(isInitLoanOptionId || isNewLoanOptionId) && (
        <p className="italic font-light">{isNewLoanOptionId ? 'Selected' : 'Unselected'}</p>
      )}
    </TableCell>
  );
};

RateTableCell.propTypes = {
  cellData: PropTypes.object.isRequired,
  programId: PropTypes.string.isRequired,
  onSelectLoanProgramOption: PropTypes.func.isRequired,
  selectedLoanProgramOption: PropTypes.object,
  pendingChanges: PropTypes.object,
  lockTerm: PropTypes.number,
  noteRate: PropTypes.number,
  programDetails: PropTypes.object,
  isProgramExpired: PropTypes.bool,
  isProgramIneligible: PropTypes.bool,
};

export default memo(RateTableCell);
